import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useWallet } from '../contexts/WalletContext';

export const useFavorites = () => {
  const { isConnected, publicKey } = useWallet();
  const [favoritePieces, setFavoritePieces] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get user wallet address (always lowercase)
  const getUserWallet = () => {
    return publicKey?.toString().toLowerCase() || 'anonymous';
  };

  // Load favorites from Supabase
  const loadFavorites = async () => {
    if (!isConnected || !publicKey) {
      setFavoritePieces(new Set());
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userWallet = getUserWallet();
      
      const { data: favorites, error: fetchError } = await supabase
        .from('favorites')
        .select('item_id')
        .eq('user_wallet', userWallet)
        .eq('item_type', 'piece');

      if (fetchError) {
        throw fetchError;
      }

      const pieces = new Set(favorites?.map(fav => fav.item_id) || []);
      console.log('✅ useFavorites: Loaded favorite piece IDs:', Array.from(pieces));
      setFavoritePieces(pieces);

    } catch (err) {
      console.error('Error loading favorites:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load favorites when wallet connects/disconnects
  useEffect(() => {
    loadFavorites();
  }, [isConnected, publicKey]);

  // Listen for favorite changes to sync across components
  useEffect(() => {
    const handleFavoritesChange = () => {
      console.log('🔄 useFavorites: Favorites changed, reloading...');
      loadFavorites();
    };

    window.addEventListener('pieceFavoritesChanged', handleFavoritesChange);
    
    return () => {
      window.removeEventListener('pieceFavoritesChanged', handleFavoritesChange);
    };
  }, [isConnected, publicKey]);

  // Toggle favorite status for pieces
  const toggleFavorite = async (pieceId) => {
    // Ensure pieceId is a string for consistent storage
    const pieceIdStr = String(pieceId);
    console.log('useFavorites toggleFavorite called with pieceId:', pieceIdStr, '(original type:', typeof pieceId, ')');
    
    if (!isConnected || !publicKey) {
      console.log('Not connected or no public key');
      console.warn('Please connect your wallet to favorite pieces');
      return false;
    }

    try {
      const userWallet = getUserWallet();
      const isCurrentlyFavorited = favoritePieces.has(pieceIdStr);
      
      console.log('useFavorites - Wallet:', userWallet, 'PieceId:', pieceIdStr, 'Currently favorited:', isCurrentlyFavorited);

      if (isCurrentlyFavorited) {
        // Remove from favorites - optimistically update UI first
        setFavoritePieces(prev => {
          const newSet = new Set(prev);
          newSet.delete(pieceIdStr);
          return newSet;
        });

        console.log('Removing from favorites...');
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_wallet', userWallet)
          .eq('item_type', 'piece')
          .eq('item_id', pieceIdStr);

        if (error) {
          console.error('Error removing favorite:', error);
          // Revert optimistic update on error
          setFavoritePieces(prev => new Set([...prev, pieceIdStr]));
          throw error;
        }
        console.log('Successfully removed from favorites');
      } else {
        // Add to favorites - optimistically update UI first
        setFavoritePieces(prev => new Set([...prev, pieceIdStr]));

        console.log('Adding to favorites...');
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_wallet: userWallet,
            item_type: 'piece',
            item_id: pieceIdStr
          });

        if (error) {
          // Handle duplicate key error - item is already favorited
          if (error.code === '23505') {
            console.log('⚠️ Item already favorited in database, state already updated');
            // State is already updated from optimistic update, so we're good
          } else {
            console.error('Error adding favorite:', error);
            // Revert optimistic update on error
            setFavoritePieces(prev => {
              const newSet = new Set(prev);
              newSet.delete(pieceIdStr);
              return newSet;
            });
            throw error;
          }
        } else {
          console.log('Successfully added to favorites');
        }
      }

      // Notify all components that piece favorites changed
      window.dispatchEvent(new Event('pieceFavoritesChanged'));
      console.log('📢 Dispatched pieceFavoritesChanged event');

      return true;
    } catch (err) {
      console.error('Error toggling favorite:', err);
      setError(err.message);
      return false;
    }
  };

  // Check if a piece is favorited
  const isFavorited = (pieceId) => {
    // Ensure pieceId is a string for consistent comparison
    const pieceIdStr = String(pieceId);
    const result = favoritePieces.has(pieceIdStr);
    console.log(`🔍 isFavorited check: pieceId="${pieceIdStr}" (original: ${typeof pieceId}), result=${result}, favoritePieces has ${favoritePieces.size} items, Set contents:`, Array.from(favoritePieces));
    return result;
  };

  // Get all favorite piece IDs
  const getFavoriteIds = () => {
    return [...favoritePieces];
  };

  // Refresh favorites from database
  const refreshFavorites = () => {
    loadFavorites();
  };

  return {
    favoritePieces,
    toggleFavorite,
    isFavorited,
    getFavoriteIds,
    loading,
    error,
    refreshFavorites,
    isConnected
  };
};
